/**
 * `CustomArtUploadModal` — TH-53369 / M5.E6.S2.2.
 *
 * Modal standalone de upload de custom art. Consumido por TH-53368 (tela
 * `/custom-arts`) e potencialmente por outras telas (gerência por loja).
 * Por isso a interface é deliberadamente isolada:
 *
 *   { open, onOpenChange, onUploaded }
 *
 * Não acessa stores, contextos ou query keys da página pai. Quem invocar
 * decide o que fazer com `onUploaded` (ex.: `queryClient.invalidateQueries`).
 *
 * DoD literal (M5.E6.S2.2):
 *   - Drag & drop com preview imediato ✓ (HTML5 native, sem dep extra)
 *   - Form: nome, escopo (loja/área opcionais), validade ✓
 *   - Validação client-side de mime/size/resolução ✓ (replica regras do
 *     backend TH-53365: PNG/JPEG/WebP, ≤ 5 MB, 1280×720 a 3840×2160)
 *   - Aviso se aspect ratio diferente de 16:9 ✓ (warning não-bloqueante,
 *     coerente com `_ASPECT_TOLERANCE` do backend)
 *   - Loading durante upload ✓ (`isPending` da mutation)
 *
 * Por que reimplementar validação que o backend já faz: feedback imediato
 * antes do upload (o operador raramente tem 50 Mbps de saída; 5 MB
 * podem demorar). Backend continua sendo source of truth — UI só evita
 * round-trip óbvio.
 */

import { useEffect, useRef, useState } from "react";
import { Upload, X as XIcon } from "lucide-react";

import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Select } from "@/components/ui/Select";
import { cn } from "@/lib/utils";
import {
  useCreateCustomArt,
  type CustomArtUploadResponse,
} from "@/hooks/useCreateCustomArt";

const ALLOWED_MIMES = ["image/png", "image/jpeg", "image/webp"] as const;
const MAX_BYTES = 5 * 1024 * 1024;
const MIN_WIDTH = 1280;
const MAX_WIDTH = 3840;
const MIN_HEIGHT = 720;
const MAX_HEIGHT = 2160;
// 16:9 = 1.7778; folga idêntica à do backend para evitar mismatch UI↔API
// quando a arte vier exportada com 1px de diferença (ex.: 1920×1081).
const TARGET_ASPECT = 16 / 9;
const ASPECT_TOLERANCE = 0.05;

export interface StoreOption {
  id: string;
  name: string;
}

export interface AreaOption {
  id: string;
  name: string;
}

export interface CustomArtUploadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Loja(s) do tenant — quem chama escolhe se mostra todas ou só visíveis ao role. */
  stores?: ReadonlyArray<StoreOption>;
  areas?: ReadonlyArray<AreaOption>;
  /** Disparado após upload bem-sucedido. Componente pai decide invalidar caches. */
  onUploaded?: (response: CustomArtUploadResponse) => void;
}

interface ImageMeta {
  width: number;
  height: number;
}

/**
 * Carrega dimensões reais via `<img>` — única forma de checar resolução
 * antes de subir. Em jsdom o callback `onload` não dispara porque o
 * canvas/img não pinta; testes mockam o construtor `Image`.
 */
export function loadImageMeta(file: File): Promise<ImageMeta> {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const { naturalWidth, naturalHeight } = img;
      URL.revokeObjectURL(objectUrl);
      resolve({ width: naturalWidth, height: naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Não foi possível ler a imagem"));
    };
    img.src = objectUrl;
  });
}

interface ValidationResult {
  fatal: string | null;
  warning: string | null;
  meta: ImageMeta | null;
}

export async function validateImageFile(file: File): Promise<ValidationResult> {
  if (!(ALLOWED_MIMES as ReadonlyArray<string>).includes(file.type)) {
    return {
      fatal: `Formato '${file.type || "desconhecido"}' não permitido — use PNG, JPEG ou WebP.`,
      warning: null,
      meta: null,
    };
  }
  if (file.size > MAX_BYTES) {
    return {
      fatal: `Arquivo excede 5 MB (${(file.size / 1024 / 1024).toFixed(1)} MB).`,
      warning: null,
      meta: null,
    };
  }
  let meta: ImageMeta;
  try {
    meta = await loadImageMeta(file);
  } catch (err) {
    return {
      fatal: err instanceof Error ? err.message : "Imagem inválida.",
      warning: null,
      meta: null,
    };
  }
  if (
    meta.width < MIN_WIDTH ||
    meta.width > MAX_WIDTH ||
    meta.height < MIN_HEIGHT ||
    meta.height > MAX_HEIGHT
  ) {
    return {
      fatal: `Resolução ${meta.width}x${meta.height} fora do permitido (${MIN_WIDTH}x${MIN_HEIGHT} a ${MAX_WIDTH}x${MAX_HEIGHT}).`,
      warning: null,
      meta,
    };
  }
  const aspect = meta.width / meta.height;
  const warning =
    Math.abs(aspect - TARGET_ASPECT) > ASPECT_TOLERANCE
      ? `Aspect ratio ${aspect.toFixed(3)} difere de 16:9 (1.778). A arte pode aparecer com barras na TV.`
      : null;
  return { fatal: null, warning, meta };
}

export function CustomArtUploadModal({
  open,
  onOpenChange,
  stores = [],
  areas = [],
  onUploaded,
}: CustomArtUploadModalProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [meta, setMeta] = useState<ImageMeta | null>(null);
  const [fatalError, setFatalError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [name, setName] = useState("");
  const [storeId, setStoreId] = useState("");
  const [areaId, setAreaId] = useState("");
  const [validFrom, setValidFrom] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);

  const mutation = useCreateCustomArt();

  // Revoga URL anterior tanto na troca de arquivo quanto no unmount —
  // dois cleanup paths separados pra não vazar quando o usuário troca
  // a imagem várias vezes antes de confirmar.
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  // Reset completo ao fechar — modal aberto de novo começa virgem.
  useEffect(() => {
    if (!open) {
      setFile(null);
      setPreviewUrl(null);
      setMeta(null);
      setFatalError(null);
      setWarning(null);
      setName("");
      setStoreId("");
      setAreaId("");
      setValidFrom("");
      setValidUntil("");
      setSubmitError(null);
      mutation.reset();
    }
  }, [open, mutation]);

  async function handleFile(selected: File) {
    setSubmitError(null);
    setFatalError(null);
    setWarning(null);
    setMeta(null);

    // Atualiza preview imediatamente, mesmo que validação reprove —
    // operador vê o que escolheu e o motivo da rejeição lado a lado.
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    const url = URL.createObjectURL(selected);
    setPreviewUrl(url);
    setFile(selected);

    const result = await validateImageFile(selected);
    setFatalError(result.fatal);
    setWarning(result.warning);
    setMeta(result.meta);
  }

  function onPickClick() {
    inputRef.current?.click();
  }

  function onInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (f) void handleFile(f);
    e.target.value = "";
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) void handleFile(f);
  }

  function onDragOver(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(true);
  }

  function onDragLeave(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    if (!file) {
      setSubmitError("Escolha uma imagem antes de enviar.");
      return;
    }
    if (fatalError) {
      setSubmitError("Corrija o erro da imagem antes de enviar.");
      return;
    }
    if (!name.trim()) {
      setSubmitError("Nome é obrigatório.");
      return;
    }
    try {
      const response = await mutation.mutateAsync({
        file,
        name: name.trim(),
        store_id: storeId || undefined,
        area_id: areaId || undefined,
        valid_from: validFrom || undefined,
        valid_until: validUntil || undefined,
      });
      onUploaded?.(response);
      onOpenChange(false);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Falha no upload.");
    }
  }

  const disabled = mutation.isPending;
  const canSubmit = !!file && !fatalError && !!name.trim() && !disabled;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogTitle>Subir arte personalizada</DialogTitle>
        <DialogDescription>
          PNG, JPEG ou WebP. Resolução entre 1280×720 e 3840×2160, até 5 MB.
        </DialogDescription>

        <form onSubmit={onSubmit} className="mt-4 space-y-4">
          <div
            data-testid="custom-art-dropzone"
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onClick={onPickClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onPickClick();
              }
            }}
            className={cn(
              "flex flex-col items-center justify-center rounded-md border-2 border-dashed",
              "border-[var(--color-border-strong)] bg-[var(--color-card)]",
              "cursor-pointer p-6 text-center text-sm",
              "hover:bg-[var(--color-secondary)]/40",
              dragOver && "border-[var(--color-primary)] bg-[var(--color-primary)]/10",
            )}
          >
            {previewUrl ? (
              <div className="flex w-full flex-col items-center gap-2">
                <img
                  src={previewUrl}
                  alt="Pré-visualização da arte"
                  className="max-h-48 max-w-full rounded-md object-contain"
                />
                {meta && (
                  <p className="text-xs text-[var(--color-muted-foreground)]">
                    {meta.width}×{meta.height} • {(file?.size ?? 0) / 1024 < 1024
                      ? `${Math.round((file?.size ?? 0) / 1024)} KB`
                      : `${((file?.size ?? 0) / 1024 / 1024).toFixed(1)} MB`}
                  </p>
                )}
                <p className="text-xs text-[var(--color-muted-foreground)]">
                  Clique ou arraste outro arquivo para substituir.
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2 text-[var(--color-muted-foreground)]">
                <Upload className="h-8 w-8" aria-hidden="true" />
                <p>Arraste a imagem aqui ou clique para escolher</p>
              </div>
            )}
            <input
              ref={inputRef}
              type="file"
              accept={ALLOWED_MIMES.join(",")}
              className="hidden"
              onChange={onInputChange}
              data-testid="custom-art-file-input"
            />
          </div>

          {fatalError && (
            <div
              role="alert"
              className="rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 px-3 py-2 text-sm text-[var(--color-destructive)]"
            >
              {fatalError}
            </div>
          )}
          {warning && !fatalError && (
            <div
              role="alert"
              data-testid="custom-art-aspect-warning"
              className="rounded-md border border-[var(--color-warning)] bg-[var(--color-warning)]/10 px-3 py-2 text-sm text-[var(--color-warning)]"
            >
              {warning}
            </div>
          )}

          <div className="grid gap-2">
            <Label htmlFor="custom-art-name">Nome *</Label>
            <Input
              id="custom-art-name"
              maxLength={200}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Banner Páscoa, comunicado interno…"
              required
              disabled={disabled}
            />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="custom-art-store">Loja (opcional)</Label>
              <Select
                id="custom-art-store"
                value={storeId}
                onChange={(e) => setStoreId(e.target.value)}
                disabled={disabled}
              >
                <option value="">Todas as lojas</option>
                {stores.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="custom-art-area">Área (opcional)</Label>
              <Select
                id="custom-art-area"
                value={areaId}
                onChange={(e) => setAreaId(e.target.value)}
                disabled={disabled}
              >
                <option value="">Todas as áreas</option>
                {areas.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="custom-art-valid-from">Válido de</Label>
              <Input
                id="custom-art-valid-from"
                type="datetime-local"
                value={validFrom}
                onChange={(e) => setValidFrom(e.target.value)}
                disabled={disabled}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="custom-art-valid-until">Válido até</Label>
              <Input
                id="custom-art-valid-until"
                type="datetime-local"
                value={validUntil}
                onChange={(e) => setValidUntil(e.target.value)}
                disabled={disabled}
              />
            </div>
          </div>

          {submitError && (
            <div
              role="alert"
              className="rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 px-3 py-2 text-sm text-[var(--color-destructive)]"
            >
              {submitError}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={disabled}
            >
              <XIcon className="h-4 w-4" aria-hidden="true" />
              Cancelar
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {disabled ? "Enviando…" : "Subir arte"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
